import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  fetchQrTranslationCatalog,
  fetchQrTranslationManifest,
  getCachedQrLanguages,
  normalizeQrLocale,
  QR_TRANSLATION_DEFAULT_LOCALE,
} from './remoteCatalog';

const TranslationCatalogContext = createContext({
  availableLocales: [],
  refreshLocale: async () => null,
});

export function TranslationCatalogProvider({children}) {
  const {i18n} = useTranslation();
  const [availableLocales, setAvailableLocales] = useState(getCachedQrLanguages);

  const applyCatalog = useCallback((catalog) => {
    if (!catalog?.l_code || !catalog?.translations) return;
    i18n.addResourceBundle(catalog.l_code, 'translation', catalog.translations, true, true);
    const codes = (catalog.available_languages || [])
      .map(language => normalizeQrLocale(language?.l_code, ''))
      .filter(Boolean);
    if (codes.length) setAvailableLocales(Array.from(new Set(codes)));
  }, [i18n]);

  const refreshLocale = useCallback(async (locale, options = {}) => {
    const lCode = normalizeQrLocale(locale);
    try {
      const catalog = await fetchQrTranslationCatalog(lCode, options);
      applyCatalog(catalog);
      return catalog;
    } catch (error) {
      // A newly removed/renamed locale should not make the language switcher stale.
      if (error?.status === 400 || error?.status === 404) {
        try {
          const manifest = await fetchQrTranslationManifest(options);
          const codes = (manifest.available_languages || [])
            .map(language => normalizeQrLocale(language?.l_code, ''))
            .filter(Boolean);
          if (codes.length) {
            const available = Array.from(new Set(codes));
            setAvailableLocales(available);
            if (!available.includes(lCode)) {
              try {
                localStorage.setItem('lang', QR_TRANSLATION_DEFAULT_LOCALE);
              } catch (storageError) {
                // i18next can still switch even when browser storage is blocked.
              }
              await i18n.changeLanguage(QR_TRANSLATION_DEFAULT_LOCALE);
            }
          }
        } catch (manifestError) {
          // Bundled resources and the last valid cache remain available offline.
        }
      }
      throw error;
    }
  }, [applyCatalog, i18n]);

  useEffect(() => {
    const controller = new AbortController();
    const currentLocale = normalizeQrLocale(
      i18n.resolvedLanguage || i18n.language,
      QR_TRANSLATION_DEFAULT_LOCALE
    );
    refreshLocale(currentLocale, {signal: controller.signal}).catch(() => {});

    const handleLanguageChanged = (locale) => {
      refreshLocale(locale).catch(() => {});
    };
    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      controller.abort();
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n, refreshLocale]);

  useEffect(() => {
    const syncDocumentLanguage = locale => {
      document.documentElement.lang = normalizeQrLocale(locale, QR_TRANSLATION_DEFAULT_LOCALE).replace('_', '-');
    };
    syncDocumentLanguage(i18n.resolvedLanguage || i18n.language);
    i18n.on('languageChanged', syncDocumentLanguage);
    return () => i18n.off('languageChanged', syncDocumentLanguage);
  }, [i18n]);

  const value = useMemo(() => ({availableLocales, refreshLocale}), [availableLocales, refreshLocale]);
  return (
    <TranslationCatalogContext.Provider value={value}>
      {children}
    </TranslationCatalogContext.Provider>
  );
}

export function useQrTranslationCatalog() {
  return useContext(TranslationCatalogContext);
}
